<script lang="ts">
  import { formatCurrency, formatDate } from '../../lib/analytics.js';
  import { Button, RadioButtonGroup, SuggestionCard } from '../common';

  import type { RecurringPattern as DetectedPattern } from '../../lib/analytics/recurring/detector';

  // Use the detector's RecurringPattern type which already has the correct recurringType
  type RecurringPattern = DetectedPattern;

  type RecurringTypeOption = 'subscription' | 'bill';

  let {
    pattern,
    isCreating = false,
    onCreateRule = () => {},
    onDismiss = () => {},
    onMarkCancelled = () => {}
  }: {
    pattern: RecurringPattern;
    isCreating?: boolean;
    onCreateRule?: (selectedType: RecurringTypeOption) => void;
    onDismiss?: () => void;
    onMarkCancelled?: (selectedType: RecurringTypeOption) => void;
  } = $props();

  // Initialize with 'subscription' by default (convert 'regular' to 'subscription')
  let selectedType = $state<RecurringTypeOption>('subscription');

  const typeOptions = [
    { value: 'subscription' as const, label: 'Subscription', icon: '🔄', description: 'Recurring service or membership' },
    { value: 'bill' as const, label: 'Bill', icon: '📄', description: 'Recurring bill or payment' }
  ];

  function handleCreateRule() {
    onCreateRule(selectedType);
  }

  function getConfidenceVariant(confidence: number): 'success' | 'warning' | 'default' {
    if (confidence > 0.8) return 'success';
    if (confidence > 0.6) return 'warning';
    return 'default';
  }

  function getConfidenceLabel(confidence: number): string {
    return `${Math.round(confidence * 100)}% match`;
  }

  const badges = [
    { label: getConfidenceLabel(pattern.confidence), variant: getConfidenceVariant(pattern.confidence) }
  ];

  function toggleType() {
    selectedType = selectedType === 'subscription' ? 'bill' : 'subscription';
  }
</script>

<SuggestionCard
  title={pattern.displayName}
  {badges}
  description={`Every ~${pattern.avgInterval} days · ${formatCurrency(pattern.avgAmount)} · ${pattern.occurrences} occurrences`}
  showCheckbox={false}
  showDetailsToggle={true}
>
  {#snippet rightContent()}
    <div class="pattern-actions">
      <div class="type-toggle">
        <button
          class="toggle-button"
          class:subscription={selectedType === 'subscription'}
          class:bill={selectedType === 'bill'}
          onclick={toggleType}
          type="button"
          disabled={isCreating}
        >
          <span class="toggle-slider"></span>
          <span class="toggle-label subscription-label">🔄 Subscription</span>
          <span class="toggle-label bill-label">📄 Bill</span>
        </button>
      </div>
      <Button
        variant="success"
        size="sm"
        onclick={handleCreateRule}
        disabled={isCreating}
      >
        {isCreating ? 'Creating...' : 'Track This'}
      </Button>
      <Button
        variant="secondary"
        size="sm"
        onclick={() => onMarkCancelled(selectedType)}
        disabled={isCreating}
      >
        Cancelled
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onclick={onDismiss}
        disabled={isCreating}
      >
        Dismiss
      </Button>
    </div>
  {/snippet}

  {#snippet children()}
    <div class="pattern-details">
      <div class="detail-row">
        <span class="label">Frequency:</span>
        <span class="value">Every ~{pattern.avgInterval} days</span>
      </div>
      <div class="detail-row">
        <span class="label">Amount:</span>
        <span class="value">
          {formatCurrency(pattern.avgAmount)}
          {#if pattern.minAmount !== pattern.maxAmount}
            <span class="range">({formatCurrency(pattern.minAmount)} - {formatCurrency(pattern.maxAmount)})</span>
          {/if}
        </span>
      </div>
      <div class="detail-row">
        <span class="label">Occurrences:</span>
        <span class="value">{pattern.occurrences}x</span>
      </div>
      <div class="detail-row">
        <span class="label">Date Range:</span>
        <span class="value text-sm">{formatDate(pattern.firstDate)} → {formatDate(pattern.lastDate)}</span>
      </div>
    </div>
  {/snippet}
</SuggestionCard>

<style lang="scss">
  .pattern-details {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: var(--font-size-sm);
    gap: var(--spacing-md);

    .label {
      color: var(--color-text-tertiary);
      font-weight: var(--font-weight-medium);
      flex-shrink: 0;
    }

    .value {
      color: var(--color-text-primary);
      font-weight: var(--font-weight-semibold);
      text-align: right;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: var(--spacing-xs);

      &.text-sm {
        font-size: var(--font-size-xs);
      }
    }

    .range {
      font-size: var(--font-size-xxs);
      color: var(--color-text-tertiary);
      font-weight: normal;
    }
  }

  .pattern-actions {
    display: flex;
    gap: var(--spacing-md);
    align-items: center;
    flex-wrap: wrap;
  }

  .type-toggle {
    display: flex;
    align-items: center;
  }

  .toggle-button {
    position: relative;
    width: 200px;
    height: 36px;
    background: var(--color-background-secondary);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-full);
    cursor: pointer;
    transition: all var(--transition-fast);
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    overflow: hidden;

    &:hover {
      border-color: var(--color-border-medium);
    }

    &:disabled {
      opacity: var(--opacity-disabled);
      cursor: not-allowed;
    }

    .toggle-slider {
      position: absolute;
      width: 50%;
      height: calc(100% - 4px);
      background: var(--color-primary);
      border-radius: var(--radius-full);
      transition: transform var(--transition-base);
      left: 2px;
      z-index: 0;
    }

    &.bill .toggle-slider {
      transform: translateX(calc(100% - 4px));
    }

    .toggle-label {
      position: relative;
      z-index: 1;
      flex: 1;
      text-align: center;
      font-size: var(--font-size-xs);
      font-weight: var(--font-weight-medium);
      transition: color var(--transition-fast);
      padding: 0 var(--spacing-xs);
      white-space: nowrap;
    }

    &.subscription .subscription-label {
      color: var(--color-background);
    }

    &.bill .subscription-label {
      color: var(--color-text-secondary);
    }

    &.subscription .bill-label {
      color: var(--color-text-secondary);
    }

    &.bill .bill-label {
      color: var(--color-background);
    }
  }

  @media (max-width: 640px) {
    .pattern-actions {
      flex-direction: column;
      width: 100%;
      align-items: stretch;
    }

    .type-toggle {
      order: -1;
      margin-bottom: var(--spacing-sm);
    }

    .toggle-button {
      width: 100%;
    }
  }
</style>
